import { useTranslation } from 'react-i18next';
import type { SalesCycle, SalesCycleImpactItem } from '@complan/contracts';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { isDefaultSelectable, type AssignmentSource } from './sales-cycle-model';

/**
 * The Sales_Cycle reassignment impact list (task 23.3, R6.7–R6.10).
 *
 * Derived from the §3.4–3.5 table/tag conventions (no dedicated Sales Cycle
 * screen exists in the UI spec; flagged in the report): a header row of muted
 * uppercase labels, one row per affected Initiative, the current and proposed
 * Sales_Cycle sets rendered as tags, and the assignment source as a badge.
 *
 * The screen shows the affected count and, per Initiative, the current-versus-
 * proposed sets and the assignment source (R6.7). Selection is per Initiative and
 * parent-owned; a row that `requiresExplicitSelection` — a manual override, or
 * status Approved or Final (R6.10) — is rendered but is not part of the default
 * selection and is flagged, so reassigning it takes a deliberate tick.
 */
export interface SalesCycleImpactListProps {
  readonly items: readonly SalesCycleImpactItem[];
  readonly totalCount: number;
  /** The year's Sales_Cycle definitions, to resolve ids to names for the tags. */
  readonly definitions: readonly SalesCycle[];
  readonly selectedIds: ReadonlySet<string>;
  readonly onToggle: (initiativeId: string) => void;
  readonly onSelectAllEligible: () => void;
  readonly onClearSelection: () => void;
  readonly onApply: () => void;
  readonly onBack: () => void;
  readonly isApplying: boolean;
}

export function SalesCycleImpactList({
  items,
  totalCount,
  definitions,
  selectedIds,
  onToggle,
  onSelectAllEligible,
  onClearSelection,
  onApply,
  onBack,
  isApplying,
}: SalesCycleImpactListProps): React.JSX.Element {
  const { t } = useTranslation();
  const nameById = new Map(definitions.map((cycle) => [cycle.id, cycle.name]));

  return (
    <Card className="gap-4 p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[13px] font-semibold text-txt">{t('salesCycle.impact.heading')}</p>
          <p className="text-muted-foreground mt-1 text-[12.3px]">
            {totalCount === 0
              ? t('salesCycle.impact.none')
              : t('salesCycle.impact.description', { count: totalCount })}
          </p>
        </div>
        <Button type="button" size="sm" variant="ghost" onClick={onBack}>
          {t('salesCycle.impact.backToForm')}
        </Button>
      </div>

      {items.length > 0 && (
        <>
          <div className="flex items-center gap-2">
            <Button type="button" size="xs" variant="outline" onClick={onSelectAllEligible}>
              {t('salesCycle.impact.selectAll')}
            </Button>
            <Button type="button" size="xs" variant="outline" onClick={onClearSelection}>
              {t('salesCycle.impact.clearSelection')}
            </Button>
          </div>

          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16 text-[10.5px] uppercase">
                  {t('salesCycle.impact.colSelect')}
                </TableHead>
                <TableHead className="text-[10.5px] uppercase">
                  {t('salesCycle.impact.colInitiative')}
                </TableHead>
                <TableHead className="text-[10.5px] uppercase">
                  {t('salesCycle.impact.colCurrent')}
                </TableHead>
                <TableHead className="text-[10.5px] uppercase">
                  {t('salesCycle.impact.colProposed')}
                </TableHead>
                <TableHead className="text-[10.5px] uppercase">
                  {t('salesCycle.impact.colSource')}
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((item) => (
                <ImpactRow
                  key={item.initiativeId}
                  item={item}
                  nameById={nameById}
                  selected={selectedIds.has(item.initiativeId)}
                  onToggle={() => onToggle(item.initiativeId)}
                />
              ))}
            </TableBody>
          </Table>

          <div>
            <Button type="button" size="sm" onClick={onApply} disabled={isApplying}>
              {isApplying ? t('salesCycle.impact.applying') : t('salesCycle.impact.apply')}
            </Button>
          </div>
        </>
      )}

      {/* An empty impact list is a legitimate answer: the definition can still be
          applied with no reassignment (R6.8). */}
      {items.length === 0 && (
        <div>
          <Button type="button" size="sm" onClick={onApply} disabled={isApplying}>
            {isApplying ? t('salesCycle.impact.applying') : t('salesCycle.impact.apply')}
          </Button>
        </div>
      )}
    </Card>
  );
}

function ImpactRow({
  item,
  nameById,
  selected,
  onToggle,
}: {
  readonly item: SalesCycleImpactItem;
  readonly nameById: ReadonlyMap<string, string>;
  readonly selected: boolean;
  readonly onToggle: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const explicit = !isDefaultSelectable(item);

  return (
    <TableRow className={cn(explicit && 'bg-muted/40')} data-state={selected ? 'selected' : undefined}>
      <TableCell>
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          aria-label={t('salesCycle.impact.colSelect')}
        />
      </TableCell>
      <TableCell>
        <span className="text-[12px] font-medium text-txt">{item.initiativeRef}</span>
        <span className="text-muted-foreground ml-1 text-[11.5px]">{item.title}</span>
        {explicit && (
          <p className="text-[11px] text-destructive">{t('salesCycle.impact.requiresExplicit')}</p>
        )}
      </TableCell>
      <TableCell>
        <CycleTags ids={item.currentSalesCycleIds} nameById={nameById} />
      </TableCell>
      <TableCell>
        <CycleTags ids={item.proposedSalesCycleIds} nameById={nameById} />
      </TableCell>
      <TableCell>
        <SourceBadge source={item.currentAssignmentSource} />
      </TableCell>
    </TableRow>
  );
}

/** A set of Sales_Cycle ids rendered as §3.4 tags; empty renders the "none" label. */
function CycleTags({
  ids,
  nameById,
}: {
  readonly ids: readonly string[];
  readonly nameById: ReadonlyMap<string, string>;
}): React.JSX.Element {
  const { t } = useTranslation();
  if (ids.length === 0) {
    return <span className="text-muted-foreground text-[11.5px]">{t('salesCycle.impact.emptySet')}</span>;
  }
  return (
    <span className="flex flex-wrap gap-1">
      {ids.map((id) => (
        <Badge key={id} variant="secondary">
          {nameById.get(id) ?? id}
        </Badge>
      ))}
    </span>
  );
}

/** The assignment-source badge (R6.7): derived or manual override. */
function SourceBadge({ source }: { source: AssignmentSource }): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <Badge variant={source === 'manual' ? 'outline' : 'ghost'}>
      {source === 'manual'
        ? t('salesCycle.impact.sourceManual')
        : t('salesCycle.impact.sourceDerived')}
    </Badge>
  );
}
